import React, { ReactNode, useEffect, useRef, useState } from 'react';
import style from './PIESideSheet.module.scss';
import PIEText from '../PIEText';
import { IconClose } from '@justeattakeaway/pie-icons-react';
import Scrollbars from 'react-custom-scrollbars';
import { isPWA } from '@src/shared/utils';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import { isDashboardAllowed } from '@src/shared/utils';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import { useInsideDrawerActions, useInsideDrawerState } from '@src/shared/context/inside-drawer';

interface componentProps {
  title: string | ReactNode;
  children?: ReactNode;
  width: number;
  isOpenInitial?: Boolean;
  onClose: () => void;
  onOpen?: () => void;
  marginTop?: number;
  scrollable?: boolean;
}

const PIESideSheet = ({
  width,
  children,
  title,
  isOpenInitial = false,
  onClose = () => {},
  onOpen = () => {},
  scrollable = true,
  marginTop = 0,
}: componentProps) => {
  const PIESheetContainer = useRef(null);
  const [isOpen, setIsOpen] = useState(isOpenInitial);
  const { isIOS } = useDeviceDetect();
  const { restaurant } = useKeycloakUserState();
  const { activeDrawerState } = useInsideDrawerState();
  const calculatePadding = () => {
    if (isPWA() && isIOS && isDashboardAllowed(restaurant?.country as string)) {
      return 50;
    } else {
      return 0;
    }
  };

  useEffect(() => {
    if (PIESheetContainer.current && isOpen) {
      const element = PIESheetContainer.current as HTMLDivElement;
      element.classList.add(style.PIESideSheetExpand);
    }
  }, [PIESheetContainer]);

  useEffect(() => {
    if (activeDrawerState) {
      setIsOpen(false);
    }
  }, [activeDrawerState]);

  useEffect(() => {
    if (!isOpen && PIESheetContainer.current) {
      const element = PIESheetContainer.current as HTMLDivElement;
      element.classList.replace(style.PIESideSheetExpand, style.PIESideSheetShrink);
    }
    if (isOpen) {
      onOpen && onOpen();
    }
  }, [isOpen]);

  return (
    <div
      ref={PIESheetContainer}
      style={
        {
          paddingTop: `${calculatePadding()}px`,
          marginTop: `${marginTop}px`,
          width: `0px`,
          '--piesidesheet-defined-width': `${width}px`,
          height: `calc(100vh - ${marginTop}px)`,
        } as any
      }
      onAnimationEnd={() => {
        if (!isOpen) {
          onClose && onClose();
        }
      }}
      className={style.PIESideSheet}
    >
      <div id="PIESideSheetContent" style={{ width }}>
        <div className={style.PIESideSheetHeader}>
          {typeof title === 'string' ? (
            <PIEText
              text={title}
              size="var(--xds-font-size-20)"
              fontWeight="var(--xds-font-weight-bold)"
              color="var(--xds-color-content-default)"
            />
          ) : (
            title
          )}
          <IconClose
            onClick={() => setIsOpen(false)}
            className={style.PIESideSheetHeaderCloseIcon}
            size="m"
          />
        </div>
        {(scrollable && (
          <Scrollbars
            style={{ height: `calc(100vh - ${marginTop * 2}px)` }}
            autoHide
            autoHideTimeout={500}
            autoHideDuration={200}
          >
            {children}
          </Scrollbars>
        )) ||
          children}
      </div>
    </div>
  );
};

export default PIESideSheet;
